import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Ban,
  CalendarClock,
  CheckCircle2,
  Coins,
  CreditCard,
  Infinity as Inf,
  Loader2,
  RotateCcw,
  ShieldAlert,
  Trash2,
  TriangleAlert,
  X,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Btn, Empty, fmtDate, fmtDay, Pill, T, useAdmin } from "@/components/admin/ui";

const TABS = ["Account", "Proposals", "Payments", "Usage", "Activity"];

const Group = ({ title, hint, icon: Icon, danger = false, children, testid }) => (
  <section
    data-testid={testid}
    className={`rounded-xl border p-4 ${
      danger
        ? "border-rose-200 dark:border-rose-900/60 bg-rose-50/40 dark:bg-rose-950/20"
        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
    }`}
  >
    <div className="flex items-start gap-2.5">
      {Icon && (
        <span
          className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${
            danger ? "bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-400" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
          }`}
        >
          <Icon className="w-3.5 h-3.5" />
        </span>
      )}
      <div className="min-w-0">
        <h3 className={`text-[13px] font-semibold ${danger ? "text-rose-700 dark:text-rose-300" : "text-slate-900 dark:text-slate-100"}`}>
          {title}
        </h3>
        {hint && <p className={`text-xs mt-0.5 ${T.muted}`}>{hint}</p>}
      </div>
    </div>
    <div className="mt-3.5 flex flex-wrap items-center gap-2">{children}</div>
  </section>
);

export const Customer360 = ({ userId, onClose, onChanged }) => {
  const { can, me } = useAdmin();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("Account");
  const [reason, setReason] = useState("");
  const [credits, setCredits] = useState(10);
  const [days, setDays] = useState(30);
  const [expiry, setExpiry] = useState("");
  const [limit, setLimit] = useState("");
  const [plan, setPlan] = useState("monthly");
  const [role, setRole] = useState("none");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/users/${userId}`);
    setD(data);
    setRole(data.platform_role || "none");
    setLimit(String(data.proposal_limit ?? ""));
  }, [userId]);

  useEffect(() => {
    setD(null);
    load().catch((e) => toast.error(apiError(e)));
  }, [load]);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const act = async (action, body = {}, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(action);
    try {
      const { data } = await api.post(`/admin/users/${userId}/${action}`, { reason, ...body });
      if (data?.deleted) {
        toast.success("Account deleted");
        onChanged?.();
        onClose();
        return;
      }
      setD(data);
      setReason("");
      toast.success(`${action.replace(/_/g, " ")} applied`);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const A = ({ id, onClick, children, variant = "ghost", disabled }) => (
    <Btn data-testid={`c360-${id}`} variant={variant} onClick={onClick} disabled={disabled || !!busy}>
      {busy === id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
      {children}
    </Btn>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="customer-360">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <motion.aside
        initial={{ x: 40, opacity: 0.6 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 320, damping: 32 }}
        className="relative w-full max-w-[880px] h-full bg-slate-50 dark:bg-slate-950 border-l border-slate-200 dark:border-slate-800 overflow-y-auto"
      >
        {!d ? (
          <div className="h-full grid place-items-center">
            <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
          </div>
        ) : (
          <>
            <header className="sticky top-0 z-10 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-800 px-5 py-4 flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <span className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 grid place-items-center text-sm font-semibold shrink-0">
                  {(d.name || d.email || "?").slice(0, 2).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-[15px] font-semibold tracking-tight truncate">{d.name || "Unnamed customer"}</h2>
                    {d.lifetime && <Pill value="lifetime" testid="c360-lifetime-pill" />}
                    <Pill value={d.disabled ? "disabled" : d.sub_status} testid="c360-status-pill" />
                    {d.platform_role && (
                      <span className="rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                        {d.platform_role.replace("_", " ")}
                      </span>
                    )}
                  </div>
                  <p className={`text-xs mt-1 truncate ${T.muted}`} data-testid="c360-email">
                    {d.email} · {d.org_name || "no workspace"} · joined {fmtDay(d.signup_date)}
                  </p>
                </div>
              </div>
              <button
                data-testid="c360-close"
                onClick={onClose}
                className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            </header>

            <div className="grid grid-cols-2 md:grid-cols-4 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
              {[
                ["Plan", d.plan_label || "—"],
                ["Credits left", `${d.proposals_left ?? 0} of ${d.proposal_limit ?? 0}`],
                ["Proposals used", d.proposals_used ?? 0],
                ["Expires", d.lifetime ? "Never" : fmtDay(d.expiry)],
              ].map(([l, v]) => (
                <div key={l} className="px-4 py-3 border-r last:border-r-0 border-slate-100 dark:border-slate-800">
                  <p className={T.label}>{l}</p>
                  <p className={`text-[15px] font-semibold mt-1 ${T.num}`}>{v}</p>
                </div>
              ))}
            </div>

            <nav className="flex gap-1 px-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
              {TABS.map((t) => (
                <button
                  key={t}
                  data-testid={`c360-tab-${t.toLowerCase()}`}
                  onClick={() => setTab(t)}
                  className={`px-3 py-2.5 text-[13px] font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                    tab === t
                      ? "border-emerald-500 text-emerald-700 dark:text-emerald-400"
                      : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                  }`}
                >
                  {t}
                </button>
              ))}
            </nav>

            <div className="p-4 sm:p-5 space-y-4">
              {tab === "Account" && (
                <>
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                    <label className={T.label} htmlFor="c360-reason">
                      Reason for this change
                    </label>
                    <input
                      id="c360-reason"
                      data-testid="c360-reason"
                      className={`${T.input} w-full mt-2`}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. goodwill credit after support ticket #421"
                    />
                    <p className={`text-xs mt-2 ${T.muted}`}>Stored permanently in the audit log next to your name.</p>
                  </div>

                  {can("users.credits") && (
                    <Group testid="c360-group-credits" title="Proposal credits" hint="Top up, claw back, or change the hard limit" icon={Coins}>
                      <input
                        data-testid="c360-credits-input"
                        type="number"
                        className={`${T.input} w-24`}
                        value={credits}
                        onChange={(e) => setCredits(Number(e.target.value))}
                      />
                      <A id="add_credits" variant="primary" onClick={() => act("add_credits", { credits })}>
                        Add credits
                      </A>
                      <A id="remove_credits" onClick={() => act("remove_credits", { credits })}>
                        Remove
                      </A>
                      {can("users.plan") && (
                        <>
                          <span className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />
                          <input
                            data-testid="c360-limit-input"
                            type="number"
                            className={`${T.input} w-28`}
                            value={limit}
                            onChange={(e) => setLimit(e.target.value)}
                            placeholder="Hard limit"
                          />
                          <A id="set_proposal_limit" onClick={() => act("set_proposal_limit", { proposal_limit: Number(limit) })}>
                            Set limit
                          </A>
                        </>
                      )}
                      {can("users.reset") && (
                        <A id="reset_usage" onClick={() => act("reset_usage", {}, "Reset the usage period for this workspace?")}>
                          <RotateCcw className="w-3 h-3" /> Reset usage
                        </A>
                      )}
                    </Group>
                  )}

                  {can("users.validity") && (
                    <Group testid="c360-group-validity" title="Validity" hint="Extend the period or pin an exact expiry date" icon={CalendarClock}>
                      <input
                        data-testid="c360-days-input"
                        type="number"
                        className={`${T.input} w-24`}
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                      />
                      <A id="extend_validity" variant="primary" onClick={() => act("extend_validity", { days })}>
                        Extend by days
                      </A>
                      <span className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />
                      <input
                        data-testid="c360-expiry-input"
                        type="date"
                        className={`${T.input} w-40`}
                        value={expiry}
                        onChange={(e) => setExpiry(e.target.value)}
                      />
                      <A id="set_expiry" onClick={() => act("set_expiry", { expiry })} disabled={!expiry}>
                        Set exact expiry
                      </A>
                    </Group>
                  )}

                  {(can("users.plan") || can("users.lifetime") || can("users.cancel")) && (
                    <Group testid="c360-group-subscription" title="Subscription" hint="Move plans, cancel, reactivate or grant lifetime" icon={CreditCard}>
                      {can("users.plan") && (
                        <>
                          <select data-testid="c360-plan-select" className={`${T.input} w-36`} value={plan} onChange={(e) => setPlan(e.target.value)}>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="yearly">Yearly</option>
                          </select>
                          <A id="change_plan" variant="primary" onClick={() => act("change_plan", { plan })}>
                            Change plan
                          </A>
                        </>
                      )}
                      {can("users.cancel") &&
                        (d.sub_status === "cancelled" ? (
                          <A id="reactivate_subscription" onClick={() => act("reactivate_subscription")}>
                            <CheckCircle2 className="w-3 h-3" /> Reactivate
                          </A>
                        ) : (
                          <A id="cancel_subscription" variant="danger" onClick={() => act("cancel_subscription", {}, "Cancel this subscription?")}>
                            Cancel subscription
                          </A>
                        ))}
                      {can("users.lifetime") &&
                        (d.lifetime ? (
                          <A
                            id="remove_lifetime"
                            variant="danger"
                            onClick={() => act("remove_lifetime", {}, "Revoke lifetime access? The workspace drops to an expired trial.")}
                          >
                            Revoke lifetime
                          </A>
                        ) : (
                          <A id="make_lifetime" variant="primary" onClick={() => act("make_lifetime", {}, "Grant permanent lifetime access?")}>
                            <Inf className="w-3 h-3" /> Grant lifetime
                          </A>
                        ))}
                    </Group>
                  )}

                  {(can("users.disable") || me?.platform_role === "super_admin") && (
                    <Group
                      testid="c360-group-danger"
                      danger
                      title="Danger zone"
                      hint="These actions affect access immediately and are always audited"
                      icon={TriangleAlert}
                    >
                      {can("users.disable") &&
                        (d.disabled ? (
                          <A id="enable_account" onClick={() => act("enable_account")}>
                            <CheckCircle2 className="w-3 h-3" /> Enable account
                          </A>
                        ) : (
                          <A id="disable_account" variant="danger" onClick={() => act("disable_account", {}, "Disable this account and end all sessions?")}>
                            <Ban className="w-3 h-3" /> Disable account
                          </A>
                        ))}
                      {me?.platform_role === "super_admin" && (
                        <>
                          <select data-testid="c360-role-select" className={`${T.input} w-40`} value={role} onChange={(e) => setRole(e.target.value)}>
                            <option value="none">No platform role</option>
                            <option value="support">Support</option>
                            <option value="admin">Admin</option>
                            <option value="super_admin">Super admin</option>
                          </select>
                          <A id="set_platform_role" onClick={() => act("set_platform_role", { platform_role: role })}>
                            <ShieldAlert className="w-3 h-3" /> Set role
                          </A>
                          <A
                            id="delete_account"
                            variant="danger"
                            onClick={() => act("delete_account", {}, `Permanently delete ${d.email}? This cannot be undone.`)}
                          >
                            <Trash2 className="w-3 h-3" /> Delete customer
                          </A>
                        </>
                      )}
                    </Group>
                  )}

                  {d.admin_actions?.length > 0 && (
                    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
                        <h3 className="text-[13px] font-semibold">Admin history on this account</h3>
                      </div>
                      <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
                        {d.admin_actions.map((a) => (
                          <li key={`${a.at}-${a.action}`} className="px-4 py-2.5 text-[13px]">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className="font-medium text-emerald-700 dark:text-emerald-400">{a.action}</span>
                              <span className={`${T.num} ${T.muted}`}>
                                {String(a.before)} → {String(a.after)}
                              </span>
                              <span className={`ml-auto text-xs ${T.muted}`}>
                                {a.admin_email} · {fmtDate(a.at)}
                              </span>
                            </div>
                            {a.reason && <p className={`text-xs mt-1 italic ${T.muted}`}>“{a.reason}”</p>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {tab === "Proposals" &&
                (d.proposals.length ? (
                  <TableCard
                    head={["Title", "Status", "Amount", "Created"]}
                    rows={d.proposals.map((p) => [p.title || "Untitled", <Pill key="s" value={p.status} />, p.amount, fmtDate(p.created_at)])}
                  />
                ) : (
                  <Empty label="No proposals yet" />
                ))}

              {tab === "Payments" &&
                (d.payments.length ? (
                  <TableCard
                    head={["Order", "Kind", "Amount", "Status", "Date"]}
                    rows={d.payments.map((p) => [
                      p.order_id,
                      `${p.kind}${p.plan ? ` · ${p.plan}` : ""}`,
                      p.amount,
                      <Pill key="s" value={p.status} />,
                      fmtDate(p.created_at),
                    ])}
                  />
                ) : (
                  <Empty label="No payments yet" />
                ))}

              {tab === "Usage" &&
                (d.api_usage.length ? (
                  <TableCard head={["Day", "API calls", "Errors"]} rows={d.api_usage.map((u) => [u.day, u.count, u.errors])} />
                ) : (
                  <Empty label="No API usage recorded" />
                ))}

              {tab === "Activity" &&
                (d.activity.length ? (
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                    <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
                      {d.activity.map((a) => (
                        <li key={`${a.at}-${a.action}-${a.target}`} className="px-4 py-2.5 flex items-center gap-3 text-[13px]">
                          <span className="font-medium text-slate-800 dark:text-slate-200">{a.action}</span>
                          <span className={`truncate ${T.muted}`}>{a.target}</span>
                          <span className={`ml-auto text-xs shrink-0 ${T.num} ${T.muted}`}>{fmtDate(a.at)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <Empty label="No workspace activity yet" />
                ))}
            </div>
          </>
        )}
      </motion.aside>
    </div>
  );
};

const TableCard = ({ head, rows }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h} className={T.th}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`row-${r[0]}-${i}`} className={T.row}>
              {r.map((c, j) => (
                <td key={j} className={`${T.td} ${j > 0 ? T.num : ""}`}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
